import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getOrders, getReviewableItems, getMyReviews } from '../lib/api';
import { getPaymentMethodName, getShippingMethodDescription } from '../lib/orderDisplay';
import AccountSidebar from '../components/AccountSidebar';
import ReviewModal from '../components/ReviewModal';
import './OrdersPage.css';

const STATUS_META = {
  delivered:        { label: 'Delivered',        color: 'var(--forest)',  bg: 'var(--mint)',  icon: '✓' },
  out_for_delivery: { label: 'Out for Delivery', color: '#0891b2',        bg: '#ecfeff',      icon: '🚚' },
  shipped:          { label: 'Shipped',          color: '#2563eb',        bg: '#eff6ff',      icon: '→' },
  processing:       { label: 'Processing',       color: '#d97706',        bg: '#fef3c7',      icon: '⋯' },
  confirmed:        { label: 'Confirmed',        color: '#7c3aed',        bg: '#f5f3ff',      icon: '✓' },
  cancelled:        { label: 'Cancelled',        color: '#dc2626',        bg: '#fef2f2',      icon: '✕' },
};

/* ── Icons ── */
function ChevronIcon({ open }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.22s ease' }}>
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}
function TruckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
      <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  );
}

function StarIcon({ filled, size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={filled ? '#F59E0B' : 'none'}
      stroke={filled ? '#F59E0B' : '#D1D5DB'}
      strokeWidth="1.5">
      <path d="M12 2.5l3.09 6.26 6.91 1-5 4.87 1.18 6.88L12 17.77l-6.18 3.74L7 14.63l-5-4.87 6.91-1L12 2.5z" />
    </svg>
  );
}

/* ── Inline review card shown after a user has reviewed an item ── */
function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const date = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <div className="ord-review-card">
      <div className="ord-review-card__header">
        <div className="ord-review-card__stars">
          {[1,2,3,4,5].map(n => <StarIcon key={n} filled={n <= review.rating} />)}
        </div>
        {review.title && <span className="ord-review-card__title">{review.title}</span>}
        <span className="ord-review-card__date">{date}</span>
      </div>
      <p className={`ord-review-card__comment${expanded ? ' ord-review-card__comment--full' : ''}`}>
        {review.comment}
      </p>
      {review.comment.length > 140 && (
        <button className="ord-review-card__toggle" onClick={() => setExpanded(v => !v)}>
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
      {review.images && review.images.length > 0 && (
        <div className="ord-review-card__photos">
          {review.images.map((img, i) => (
            <a key={i} href={img.url} target="_blank" rel="noreferrer">
              <img src={img.url} alt={`Review photo ${i + 1}`} className="ord-review-card__photo" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Single order item row with product image + variants + review state ── */
function OrderItem({ item, itemIndex, order, reviewedMap, reviewsMap, onWriteReview }) {
  const key = `${order._id}_${itemIndex}`;
  const reviewed = reviewedMap[key];
  const review = reviewsMap[key];

  const variants = [item.selectedSize, item.selectedPlanter, item.selectedColor].filter(Boolean);
  const isBundle = item.itemType === 'bundle';

  return (
    <div className="ord-item">
      {/* Product image */}
      <div className="ord-item__img-wrap">
        {item.image
          ? <img src={item.image} alt={item.name} className="ord-item__img" />
          : <div className="ord-item__img-placeholder">🌿</div>
        }
      </div>

      {/* Name + variants + bundle plants */}
      <div className="ord-item__info">
        <span className="ord-item__name">
          {item.name}
          {isBundle && <span className="ord-bundle-badge">Bundle</span>}
        </span>
        {variants.length > 0 && (
          <p className="ord-item__variants">{variants.join(' · ')}</p>
        )}
        {/* Bundle: list the individual plants with variant image + details */}
        {isBundle && item.bundleItems && item.bundleItems.length > 0 && (
          <div className="ord-bundle-plants">
            <p className="ord-bundle-plants__label">Includes:</p>
            {item.bundleItems.map((bi, i) => {
              const biVariants = [bi.selectedSize, bi.selectedPlanter, bi.selectedColor].filter(Boolean);
              return (
                <div key={i} className="ord-bundle-plant">
                  <div className="ord-bundle-plant__img-wrap">
                    {bi.image
                      ? <img src={bi.image} alt={bi.name} className="ord-bundle-plant__img"
                          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                      : null}
                    <div className="ord-bundle-plant__fallback" style={bi.image ? { display: 'none' } : {}}>🌿</div>
                  </div>
                  <div className="ord-bundle-plant__details">
                    <span className="ord-bundle-plant__name">{bi.name}</span>
                    {biVariants.length > 0 && (
                      <span className="ord-bundle-plant__variants">{biVariants.join(' · ')}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <span className="ord-item__qty-mobile">Qty: {item.qty}</span>
      </div>

      {/* Qty */}
      <span className="ord-item__qty">× {item.qty}</span>

      {/* Price */}
      <span className="ord-item__price">₹{(item.price * item.qty).toLocaleString('en-IN')}</span>

      {/* Review section — only for delivered orders with a known productId */}
      {order.status === 'delivered' && item.productId && (
        <div className="ord-item__review-section">
          {reviewed && review ? (
            <ReviewCard review={review} />
          ) : reviewed ? (
            <div className="ord-item__review-done">
              <span className="ord-item__review-done__stars">
                {[1,2,3,4,5].map(n => <StarIcon key={n} filled={n <= (review?.rating || 5)} />)}
              </span>
              <span>✓ You reviewed this product</span>
            </div>
          ) : (
            <button
              className="ord-item__review-btn"
              onClick={() => onWriteReview(order, item, itemIndex)}
            >
              ✏️ Write a Review
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Order Card ── */
function OrderCard({ order, reviewedMap, reviewsMap, onWriteReview }) {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[order.status] || STATUS_META.processing;

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return iso; }
  };

  const addressStr = order.address
    ? [order.address.address, order.address.apartment, order.address.city, order.address.state, order.address.pincode].filter(Boolean).join(', ')
    : '—';

  return (
    <div className={`ord-card${open ? ' ord-card--open' : ''}`}>
      {/* Header row */}
      <button className="ord-card__header" onClick={() => setOpen(v => !v)}>
        <div className="ord-card__meta">
          <span className="ord-card__id">Order {order.orderId}</span>
          <span className="ord-card__date">{formatDate(order.createdAt)}</span>
        </div>
        <div className="ord-card__right">
          <span className="ord-badge" style={{ color: meta.color, background: meta.bg }}>
            <span>{meta.icon}</span> {meta.label}
          </span>
          <span className="ord-card__total">₹{order.finalTotal.toLocaleString('en-IN')}</span>
          <span className="ord-card__chevron"><ChevronIcon open={open} /></span>
        </div>
      </button>

      {/* Expanded detail */}
      {open && (
        <div className="ord-card__body">
          {/* Items */}
          <div className="ord-section">
            <p className="ord-section__label">Items ordered</p>
            <div className="ord-items">
              {order.items.map((item, i) => (
                <OrderItem
                  key={i}
                  item={item}
                  itemIndex={i}
                  order={order}
                  reviewedMap={reviewedMap}
                  reviewsMap={reviewsMap}
                  onWriteReview={onWriteReview}
                />
              ))}

              {order.discount > 0 && (
                <div className="ord-item ord-item--discount">
                  <div className="ord-item__img-wrap" />
                  <div className="ord-item__info">
                    <span className="ord-item__name" style={{ color: 'var(--forest)' }}>
                      🏷 Discount {order.promoCode ? `(${order.promoCode})` : ''}
                    </span>
                  </div>
                  <span className="ord-item__qty" />
                  <span className="ord-item__price" style={{ color: 'var(--forest)' }}>
                    −₹{order.discount.toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              <div className="ord-item ord-item--total">
                <div className="ord-item__img-wrap" />
                <div className="ord-item__info">
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Order total</span>
                </div>
                <span className="ord-item__qty" />
                <span className="ord-item__price ord-item__price--bold">
                  ₹{order.finalTotal.toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          {/* Tracking + address */}
          <div className="ord-detail-grid">
            <div className="ord-section">
              <p className="ord-section__label">Delivery address</p>
              <p className="ord-detail__value">{addressStr}</p>
            </div>
            <div className="ord-section">
              <p className="ord-section__label">Payment method</p>
              <p className="ord-detail__value">{getPaymentMethodName(order)}</p>
            </div>
            <div className="ord-section">
              <p className="ord-section__label">Shipping method</p>
              <p className="ord-detail__value">{getShippingMethodDescription(order)}</p>
            </div>
            {order.tracking && (
              <div className="ord-section">
                <p className="ord-section__label">Tracking number</p>
                <p className="ord-detail__value ord-detail__tracking">{order.tracking}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="ord-actions">
            <Link to={`/account/orders/${order.orderId}`} className="ord-btn ord-btn--outline">
              <TruckIcon /> View Order Status
            </Link>
            {order.status === 'delivered' && (
              <Link to="/collections/plants" className="ord-btn ord-btn--outline">Buy Again</Link>
            )}
            {(order.status === 'shipped' || order.status === 'out_for_delivery') && (
              <button className="ord-btn ord-btn--outline">
                <TruckIcon /> Track Shipment
              </button>
            )}
            <button className="ord-btn ord-btn--ghost">Download Receipt</button>
          </div>
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 12;

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  pages.push(1);
  if (page > 3) pages.push('…');
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) pages.push(p);
  if (page < totalPages - 2) pages.push('…');
  if (totalPages > 1) pages.push(totalPages);
  return (
    <nav className="ord-pagination" aria-label="Orders pages">
      <button className="ord-page-btn ord-page-btn--nav" onClick={() => onChange(page - 1)} disabled={page === 1} aria-label="Previous">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="ord-page-ellipsis">…</span>
        ) : (
          <button key={p} className={`ord-page-btn${p === page ? ' ord-page-btn--active' : ''}`} onClick={() => onChange(p)} aria-current={p === page ? 'page' : undefined}>{p}</button>
        )
      )}
      <button className="ord-page-btn ord-page-btn--nav" onClick={() => onChange(page + 1)} disabled={page === totalPages} aria-label="Next">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
    </nav>
  );
}

/* ── Main component ── */
export default function OrdersPage({ user, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(1);
  const [reviewedMap, setReviewedMap] = useState({});   // `${orderMongoId}_${itemIndex}` -> true
  const [reviewsMap, setReviewsMap] = useState({});     // `${orderMongoId}_${itemIndex}` -> Review object
  const [reviewTarget, setReviewTarget] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user) { navigate('/account'); return; }
    fetchOrders();
    fetchReviewData();
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (location.state?.newOrder) setFilter('all');
  }, [location.state]);

  const fetchOrders = async () => {
    setLoading(true);
    setFetchError('');
    try {
      const data = await getOrders();
      setOrders(data);
    } catch (err) {
      setFetchError(err.message || 'Failed to load orders.');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewData = async () => {
    try {
      // Fetch both: which items are reviewable/reviewed, and the full review objects
      const [{ orders: reviewable }, { reviews }] = await Promise.all([
        getReviewableItems(),
        getMyReviews(),
      ]);

      // Build reviewedMap: key -> boolean
      const reviewed = {};
      reviewable.forEach(o => {
        o.items.forEach(item => {
          reviewed[`${o._id}_${item.orderItemIndex}`] = item.reviewed;
        });
      });
      setReviewedMap(reviewed);

      // Build reviewsMap: key -> full review object
      // The review has order (ObjectId) and orderItemIndex — match against reviewable orders
      const rMap = {};
      reviews.forEach(rev => {
        const orderId = typeof rev.order === 'object' ? rev.order._id || rev.order : rev.order;
        rMap[`${orderId}_${rev.orderItemIndex}`] = rev;
      });
      setReviewsMap(rMap);
    } catch {
      /* non-critical */
    }
  };

  const handleWriteReview = (order, item, orderItemIndex) => {
    setReviewTarget({ order, item: { ...item, orderItemIndex }, orderId: order.orderId });
  };

  const handleReviewSubmitted = (review) => {
    if (reviewTarget) {
      const key = `${reviewTarget.order._id}_${reviewTarget.item.orderItemIndex}`;
      setReviewedMap(prev => ({ ...prev, [key]: true }));
      if (review) {
        setReviewsMap(prev => ({ ...prev, [key]: review }));
      }
    }
    setReviewTarget(null);
  };

  const FILTERS = [
    { key: 'all',              label: 'All Orders' },
    { key: 'confirmed',        label: 'Confirmed' },
    { key: 'delivered',        label: 'Delivered' },
    { key: 'shipped',          label: 'Shipped' },
    { key: 'out_for_delivery', label: 'Out for Delivery' },
    { key: 'cancelled',        label: 'Cancelled' },
  ];

  const filtered = orders.filter(o => {
    const matchStatus = filter === 'all' || o.status === filter;
    const matchSearch = !search || o.orderId.toLowerCase().includes(search.toLowerCase()) ||
      o.items.some(i => i.name.toLowerCase().includes(search.toLowerCase()));
    return matchStatus && matchSearch;
  });

  // Reset to page 1 when filter or search changes
  useEffect(() => { setPage(1); }, [filter, search]);

  const totalPages   = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage     = Math.min(page, totalPages);
  const pagedOrders  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const goToPage = p => { setPage(p); window.scrollTo(0, 0); };

  return (
    <div className="ord-page">
      <AccountSidebar user={user} onLogout={onLogout} active="orders" />

      <main className="ord-main">
        <div className="ord-header">
          <div>
            <h1 className="ord-title">Your Orders</h1>
            <p className="ord-subtitle">
              {loading ? 'Loading…' : `${orders.length} order${orders.length !== 1 ? 's' : ''} placed`}
            </p>
          </div>
          <Link to="/collections/plants" className="ord-btn ord-btn--primary">Shop More Plants</Link>
        </div>

        <div className="ord-controls">
          <div className="ord-filters">
            {FILTERS.map(f => (
              <button
                key={f.key}
                className={`ord-filter${filter === f.key ? ' ord-filter--active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ord-search-wrap">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ord-search-icon">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="ord-search"
              type="text"
              placeholder="Search orders or products…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading && (
          <div className="ord-skel-list">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="ord-skel-card">
                <div className="ord-skel-card__header">
                  <div className="skel" style={{ width: 120, height: 13, borderRadius: 5 }} />
                  <div className="skel" style={{ width: 80, height: 22, borderRadius: 20 }} />
                </div>
                <div className="ord-skel-card__items">
                  {[...Array(2)].map((_, j) => (
                    <div key={j} className="ord-skel-item">
                      <div className="ord-skel-item__img skel" />
                      <div className="ord-skel-item__info">
                        <div className="skel" style={{ width: '60%', height: 13, borderRadius: 5 }} />
                        <div className="skel" style={{ width: '35%', height: 12, borderRadius: 5, marginTop: 6 }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ord-skel-card__footer">
                  <div className="skel" style={{ width: 100, height: 13, borderRadius: 5 }} />
                  <div className="skel" style={{ width: 80, height: 13, borderRadius: 5 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && fetchError && (
          <div className="ord-empty">
            <span className="ord-empty__emoji">⚠️</span>
            <h3>Couldn't load orders</h3>
            <p>{fetchError}</p>
            <button className="ord-btn ord-btn--primary" onClick={fetchOrders} style={{ display: 'inline-flex', width: 'auto', padding: '0 28px', marginTop: '12px' }}>
              Retry
            </button>
          </div>
        )}

        {!loading && !fetchError && filtered.length === 0 && (
          <div className="ord-empty">
            <span className="ord-empty__emoji">🌿</span>
            <h3>{orders.length === 0 ? 'No orders yet' : 'No orders found'}</h3>
            <p>{orders.length === 0 ? 'Place your first order and it will appear here.' : 'Try a different filter or search term.'}</p>
            <Link to="/collections/plants" className="ord-btn ord-btn--primary" style={{ display: 'inline-flex', width: 'auto', padding: '0 28px' }}>
              Browse Plants
            </Link>
          </div>
        )}

        {!loading && !fetchError && filtered.length > 0 && (
          <>
            <div className="ord-list">
              {pagedOrders.map(order => (
                <OrderCard
                  key={order._id || order.orderId}
                  order={order}
                  reviewedMap={reviewedMap}
                  reviewsMap={reviewsMap}
                  onWriteReview={handleWriteReview}
                />
              ))}
            </div>
            <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />
          </>
        )}
      </main>

      <ReviewModal
        open={!!reviewTarget}
        item={reviewTarget?.item}
        orderId={reviewTarget?.orderId}
        onClose={() => setReviewTarget(null)}
        onSubmitted={handleReviewSubmitted}
      />
    </div>
  );
}